import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, Input, OnInit, ViewChild, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import {
  MatExpansionPanel,
  MatExpansionPanelDescription,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatHint, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { Alert, AlertService, AlertType, IconModule, LinkModule, LoaderModule } from '@michelin/theme';
import { NgxCsvParser } from 'ngx-csv-parser';
import { Error } from 'src/app/interfaces/error';
import { ImportFileCsv } from 'src/app/interfaces/importFileCsv';
import { ImportFileCsvStatus } from 'src/app/interfaces/importFileCsvStatus';
import { UtilService } from 'src/app/util/util.service';
import { BACKEND_URL } from '../../../environments/environment';
import { ImportFileModalComponent } from '../import-file-modal/import-file-modal.component';
import { HistoricModalComponent } from './historic-modal/historic-modal.component';
import { ImportFileService } from './import-file.service';

@Component({
  selector: 'mic-app-import-file',
  templateUrl: './import-file.component.html',
  styleUrls: ['./import-file.component.scss'],
  imports: [
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    MatFormField,
    MatLabel,
    MatInput,
    MatHint,
    MatButton,
    MatSuffix,
    MatTooltip,
    IconModule,
    MatIcon,
    LoaderModule,
    LinkModule
  ]
})
export class ImportFileComponent implements OnInit {
  private readonly ngxCsvParser = inject(NgxCsvParser);
  private readonly fileService = inject(ImportFileService);
  dialog = inject(MatDialog);
  readonly utilService = inject(UtilService);
  readonly alertService = inject(AlertService);
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly B = 0xef;
  private readonly O = 0xbb;
  private readonly M = 0xbf;

  csvRecords: any;
  header = true;

  progressBar = 0;
  errors: Error[] = [];
  user = JSON.parse(localStorage.getItem('user'));
  panelOpenState = false;

  displayStatistique = false;
  displayStatistiqueF2 = false;
  intervalId: any;
  @Input() func: any;
  importFileCsvStatus: ImportFileCsvStatus;
  selectedSimpleFileInput: FileList;
  fileSend: File;
  @ViewChild('fileImportInput') fileImportInput: any;
  test: string;

  ngOnInit(): void {
    console.log('the status ', this.func.lastStatus);
    if (this.func.lastStatus !== null) {
      this.importFileCsvStatus = this.func.lastStatus;
      this.fileService.getStatus(this.importFileCsvStatus.id).subscribe((response) => {
        this.importFileCsvStatus = response;
        console.log('Test the importFileCsvStatus ', this.importFileCsvStatus.status);
        if (this.importFileCsvStatus.status === 'in progress') {
          this.displayStatistique = true;
          this.cdr.markForCheck();
        }
        this.progressBar = parseInt(localStorage.getItem('progressBar'));
        this.cdr.markForCheck();
      });
    }
  }

  onHistoric() {
    this.dialog.open(HistoricModalComponent, {
      width: '800px',
      data: this.func.id
    });
  }

  openUpdatedCsvFunction() {
    const dialogRef = this.dialog.open(ImportFileModalComponent, {
      width: '550px',
      data: this.func
    });
    dialogRef.afterClosed().subscribe((importFileCsv: ImportFileCsv | boolean) => {
      if (importFileCsv !== undefined && importFileCsv !== false) {
        this.func = importFileCsv as ImportFileCsv;
      }
    });
  }

  selectSimpleFileInput($event: any) {
    this.selectedSimpleFileInput = $event.target.files;
    this.fileSend = this.selectedSimpleFileInput[0];
    this.header = (this.header as unknown as string) === this.func.header.toString() || this.header === this.func.header;
    this.ngxCsvParser
      .parse(this.selectedSimpleFileInput[0], { header: this.func.header, delimiter: this.func.delimiter })
      .pipe()
      .subscribe({
        next: (result): void => {
          this.csvRecords = result;
          this.displayStatistique = false;
        },
        error: (): void => {}
      });
  }

  /**
   * Handles the send action.
   * If the header is available, it validates the file.
   * Otherwise, it reads the file and uploads it.
   * Updates the status periodically.
   */
  onSend() {
    if (this.func.secondFunction) {
      this.displayStatistiqueF2 = true;
      console.log('the status ', this.displayStatistiqueF2);
      if (this.importFileCsvStatus) {
        this.importFileCsvStatus.errorList.length = 0;
      }
    }
    if (this.func.header) {
      this.validateFile();
    } else {
      this.readFileAndUpload();
    }
  }

  /**
   * Validates the file and performs the necessary actions based on the validation result.
   */
  validateFile() {
    if (this.isValidFile()) {
      this.readFileAndUpload();
    } else {
      this.alertInvalidFile();
    }
  }

  /**
   * Checks if the imported CSV file has a valid number of columns.
   *
   * @returns  True if the number of columns in the CSV file matches the expected number, false otherwise.
   */
  isValidFile(): boolean {
    return Object.keys(this.csvRecords[0]).length === this.func.columnCsvList.length;
  }

  alertInvalidFile() {
    alert("it's not a valid file");
  }

  /**
   * Reads the selected file and uploads it.
   */
  readFileAndUpload() {
    const fileReader = new FileReader();
    fileReader.onload = () => this.handleFileLoad(fileReader.result);
    fileReader.readAsArrayBuffer(this.fileSend);
  }

  /**
   * Handles the file load event.
   *
   * @param fileResult - The result of the file load operation.
   */
  handleFileLoad(fileResult: any) {
    const data = new Uint8Array(fileResult);
    if (this.isBOMPresent(data)) {
      this.showAlertForBOM();
    } else {
      this.uploadFile();
    }
  }

  /**
   * Checks if the Byte Order Mark (BOM) is present in the given data.
   *
   * @param data - The data to check for BOM presence.
   * @returns A boolean value indicating whether the BOM is present or not.
   */
  isBOMPresent(data: any) {
    return data[0] === this.B && data[1] === this.O && data[2] === this.M;
  }

  /**
   * Shows an alert for BOM (Byte Order Mark) encoding error.
   *
   * @remarks
   * This method displays an alert with a critical type, indicating an encoding error.
   * The content of the alert message informs the user that the encoding of the file is not correct,
   * and requests them to recheck the encoding. The accepted encoding is UTF-8 without BOM.
   */
  showAlertForBOM() {
    this.alertService.showAlert({
      type: AlertType.critical,
      title: 'Encoding Error',
      content: "The encoding of the File is not correct can you please recheck. The encoding accepted it's UTF-8 without BOM"
    } as Alert);
  }

  /**
   * Uploads the file using the file service.
   *
   * @remarks
   * This method calls the `upload` method of the file service to upload the file.
   * It subscribes to the upload event and handles the success and error cases.
   */
  uploadFile() {
    this.fileService.upload(this.func.id, this.fileSend).subscribe(
      (event) => this.handleUploadSuccess(event),
      (error: HttpErrorResponse) => this.handleUploadError(error)
    );
  }

  uploadCsvFileError() {
    const csv = this.importFileCsvStatus.errorList
      .map((row) => row.lineContent)
      .filter((lineContent) => lineContent !== null)
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'errors.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Handles the successful upload of a file.
   *
   * @param event - The event object containing the upload details.
   */
  handleUploadSuccess(event: any) {
    console.log('the result from the backend ', event);
    this.importFileCsvStatus = event;
    this.progressBar = this.csvRecords.length;

    localStorage.setItem('progressBar', this.progressBar.toString());

    if (this.func.secondFunction) {
      this.displayStatistiqueF2 = false;
      this.cdr.markForCheck();
    } else {
      this.displayStatistique = true;
      this.cdr.markForCheck();
    }
    console.log('the status ', this.displayStatistiqueF2);
    this.test = `${BACKEND_URL.url}downloadFile/${this.importFileCsvStatus.id}`;
    this.cdr.markForCheck();
    this.updateStatusPeriodically();
  }

  /**
   * Handles the upload error and displays an alert with the error message.
   *
   * @param error - The error object containing the error message.
   */
  handleUploadError(error: any) {
    console.log('the error ', error);
    this.importFileCsvStatus = error.error.importFileCsvStatus;
    console.log('test', this.importFileCsvStatus.errorList);
    // this.importFileCsvStatus.status = 'KO';
    this.displayStatistiqueF2 = false;
    this.cdr.markForCheck();
    this.alertService.showAlert({
      type: AlertType.critical,
      content: error.error.message
    } as Alert);
  }

  /**
   * Updates the status of the import file periodically.
   * This method sets an interval to check the status of the import file at regular intervals.
   * If the status is 'OK' or 'KO', the interval is cleared and the displayStatistique flag is set to false.
   * Otherwise, the displayStatistique flag is set to true.
   * The status is retrieved by calling the getStatus method of the fileService.
   */
  updateStatusPeriodically() {
    // Clear any existing polling to prevent duplicate loops
    if (this.intervalId) {
      clearTimeout(this.intervalId);
      this.intervalId = null;
    }

    const poll = () => {
      if (
        this.importFileCsvStatus !== undefined &&
        (this.importFileCsvStatus.status === 'OK' || this.importFileCsvStatus.status === 'KO')
      ) {
        this.intervalId = null;
        this.displayStatistique = false;
        this.cdr.markForCheck();
        return;
      }

      this.displayStatistique = true;
      this.cdr.markForCheck();
      this.fileService.getStatus(this.importFileCsvStatus.id).subscribe({
        next: (response) => {
          this.importFileCsvStatus = response;
          this.cdr.markForCheck();
          // Schedule next poll only after the current one completes
          this.intervalId = setTimeout(poll, 2000);
        },
        error: () => {
          // On error, retry after a longer delay to avoid hammering the server
          this.intervalId = setTimeout(poll, 5000);
        }
      });
    };

    // Start first poll after a short delay
    this.intervalId = setTimeout(poll, 2000);
  }

  /**
   * Uploads a CSV file and triggers a file download.
   */
  uploadCsvFile() {
    this.fileService.downloadFile(this.importFileCsvStatus.id).subscribe((response) => {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(response.body);
      link.download = this.importFileCsvStatus.fileName;
      link.click();
      URL.revokeObjectURL(link.href);
    });
  }

  /**
   * Stops the treatment by clearing the interval and hiding the statistics display.
   */
  onStopTheTreatment() {
    if (this.intervalId) {
      clearTimeout(this.intervalId);
      this.intervalId = null;
    }
    this.displayStatistique = false;
    this.displayStatistiqueF2 = false;
    this.importFileCsvStatus.status = 'KO';
  }
}
